import { useState } from "react";
import { formatPence, formatTokenUnits } from "../lib/money";
import {
  exportWalletKey,
  refreshLiveBalances,
  resetApp,
  retryRoundup,
  topUpReserve,
  updateSettings,
  useAppState,
} from "../lib/store";
import type { Hex } from "../lib/types";
import { CardDetails } from "./CardDetails";
import { TapToPay } from "./TapToPay";
import { MoneySection } from "./MoneySection";

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="kv">
      <span className="k">{label}</span>
      <span className="v" style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
        <span style={{ fontSize: 12 }}>{value.slice(0, 10)}…{value.slice(-6)}</span>
        <button
          className="btn-ghost"
          style={{ padding: "2px 8px" }}
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? "✓" : "Copy"}
        </button>
      </span>
    </div>
  );
}

export function SettingsScreen() {
  const s = useAppState();
  const [safeInput, setSafeInput] = useState(s.settings.safeAddress ?? "");
  const [keyRevealed, setKeyRevealed] = useState(false);
  const [resetRevealed, setResetRevealed] = useState(false);
  const failed = s.roundups.filter((r) => r.status === "failed");
  const lb = s.liveBalances;

  const saveSafe = () => {
    const addr = safeInput.trim();
    if (/^0x[0-9a-fA-F]{40}$/.test(addr)) {
      void updateSettings({ safeAddress: addr as Hex, demoMode: false });
    }
  };

  return (
    <div className="screen">
      <h1>Settings</h1>

      <h2>Round-up reserve</h2>
      <div className="card">
        <div className="kv">
          <span className="k">Balance</span>
          <span className="v">{formatPence(s.reservePence)}</span>
        </div>
        {s.settings.demoMode ? (
          <>
            <div className="row" style={{ marginTop: 10 }}>
              <button className="btn-secondary" onClick={() => void topUpReserve(500)}>
                +£5
              </button>
              <button className="btn-secondary" onClick={() => void topUpReserve(1000)}>
                +£10
              </button>
              <button className="btn-secondary" onClick={() => void topUpReserve(2000)}>
                +£20
              </button>
            </div>
            <p className="sub" style={{ marginTop: 10, marginBottom: 0 }}>
              Demo reserve — in live mode this is the USDC.e balance of your investment wallet on
              Gnosis Chain.
            </p>
          </>
        ) : (
          <>
            <p className="sub" style={{ marginTop: 8, marginBottom: 0 }}>
              Top up from “Money in” below — a normal bank transfer. Processing costs come out of
              your deposits automatically, never on top.
            </p>
            <div style={{ marginTop: 10 }}>
              <button className="btn-secondary" onClick={() => void refreshLiveBalances()}>
                Refresh balances
              </button>
            </div>
            <details style={{ marginTop: 10 }}>
              <summary className="sub" style={{ cursor: "pointer" }}>
                Advanced (for crypto-savvy users)
              </summary>
              {s.walletAddress && <CopyRow label="Fund this address" value={s.walletAddress} />}
              <div className="kv">
                <span className="k">USDC.e (reserve)</span>
                <span className="v">{lb ? `$${formatTokenUnits(lb.reserveUnits, 6, 2)}` : "—"}</span>
              </div>
              <div className="kv">
                <span className="k">xDAI (Gnosis gas)</span>
                <span className="v">{lb ? formatTokenUnits(lb.xdaiWei, 18, 3) : "—"}</span>
              </div>
              <div className="kv">
                <span className="k">ETH on Robinhood Chain</span>
                <span className="v">{lb ? formatTokenUnits(lb.destEthWei, 18, 5) : "—"}</span>
              </div>
              <p className="sub" style={{ marginTop: 8, marginBottom: 0 }}>
                Direct funding: USDC.e on Gnosis Chain to the address above. Gas on both chains is
                auto-replenished from deposits/round-ups after first activation.
              </p>
            </details>
          </>
        )}
      </div>

      <h2>Mode</h2>
      <div className="option-list">
        <button
          className={`option ${s.settings.demoMode ? "selected" : ""}`}
          onClick={() => void updateSettings({ demoMode: true })}
        >
          <span>
            Demo mode
            <span className="option-sub">Simulated purchases, bridge and swap</span>
          </span>
          {s.settings.demoMode && <span className="tick">✓</span>}
        </button>
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="balance-label" style={{ marginBottom: 8 }}>
            Live mode — watch a Gnosis Pay Safe
          </div>
          <input
            type="text"
            placeholder="0x… your Gnosis Pay Safe address"
            value={safeInput}
            onChange={(e) => setSafeInput(e.target.value)}
            spellCheck={false}
            autoCapitalize="none"
          />
          <div style={{ marginTop: 10 }}>
            <button
              className="btn-secondary"
              onClick={saveSafe}
              disabled={!/^0x[0-9a-fA-F]{40}$/.test(safeInput.trim())}
            >
              Watch this Safe
            </button>
          </div>
          {!s.settings.demoMode && s.settings.safeAddress && (
            <p className="sub" style={{ margin: "10px 0 0" }}>
              Watching {s.settings.safeAddress.slice(0, 10)}…{s.settings.safeAddress.slice(-6)}.
              Card spends detected on-chain invest for real via LiFi → Robinhood Chain.
            </p>
          )}
        </div>
      </div>

      {failed.length > 0 && (
        <>
          <h2>Failed round-ups</h2>
          <div className="card">
            {failed.map((r) => (
              <div className="kv" key={r.id}>
                <span className="k">
                  {r.merchant} · {formatPence(r.roundupPence, r.currency)}
                  {r.statusDetail && (
                    <span style={{ display: "block", fontSize: 11 }}>{r.statusDetail}</span>
                  )}
                </span>
                <button className="btn-ghost" onClick={() => void retryRoundup(r.id)}>
                  Retry
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <h2>Card</h2>
      {!s.settings.demoMode && <CardDetails />}
      <TapToPay />

      <MoneySection />

      <h2>Debug</h2>
      <div className="card">
        <div className="kv">
          <span className="k">Investment wallet</span>
          <span className="v">{s.walletAddress ?? "—"}</span>
        </div>
        <div className="kv">
          <span className="k">Watched Safe</span>
          <span className="v">{s.settings.safeAddress ?? "not set"}</span>
        </div>
        <div className="kv">
          <span className="k">Last scanned block</span>
          <span className="v">{s.lastScannedBlock ?? "—"}</span>
        </div>
        <div className="kv">
          <span className="k">Gnosis RPC</span>
          <span className="v">
            <span className={`pill ${s.rpcOk ? "ok" : "bad"}`}>{s.rpcOk ? "OK" : "ERROR"}</span>
          </span>
        </div>
        <div className="kv">
          <span className="k">Pending records</span>
          <span className="v">
            {s.roundups.filter((r) => !["complete", "skipped"].includes(r.status)).length}
          </span>
        </div>
        {s.errors.length > 0 && (
          <div className="kv">
            <span className="k">Last error</span>
            <span className="v">{s.errors[s.errors.length - 1]}</span>
          </div>
        )}
      </div>

      <h2>Wallet key</h2>
      <div className="card">
        {!keyRevealed ? (
          <button className="btn-secondary" onClick={() => setKeyRevealed(true)}>
            Reveal private key
          </button>
        ) : (
          <>
            <p className="sub" style={{ marginBottom: 8 }}>
              Anyone with this key controls the reserve and your stock tokens. Store it safely, then
              hide it.
            </p>
            <code style={{ fontSize: 11, wordBreak: "break-all" }}>{exportWalletKey()}</code>
            <div style={{ marginTop: 10 }}>
              <button className="btn-secondary" onClick={() => setKeyRevealed(false)}>
                Hide
              </button>
            </div>
          </>
        )}
      </div>

      <h2>Danger zone</h2>
      {!resetRevealed ? (
        <button className="btn-secondary" onClick={() => setResetRevealed(true)} style={{ marginBottom: 10 }}>
          Show reset option
        </button>
      ) : (
        <button
          className="btn-danger"
          onClick={() => {
            if (confirm("Wipe all local data, wallet key included? Funds on that key will be unrecoverable unless you exported it.")) void resetApp();
          }}
          style={{ marginBottom: 10 }}
        >
          Reset app & wipe local data
        </button>
      )}
    </div>
  );
}
